import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  Building2,
  Gavel,
  Wrench,
  ShieldAlert,
  ListChecks,
  Activity,
  ExternalLink,
  Settings,
} from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const HEALTH_COLORS = {
  green: "text-[#A3C293]",
  yellow: "text-champagne",
  red: "text-[#E5A09E]",
};

const HealthBadge = ({ health }) => (
  <span className={`inline-flex items-center gap-2 text-xs uppercase tracking-wider ${HEALTH_COLORS[health] || "text-textmuted"}`}>
    <span className={`w-2 h-2 rounded-full ${health === "green" ? "bg-[#A3C293]" : health === "red" ? "bg-[#E5A09E]" : "bg-champagne"}`} />
    {health || "unknown"}
  </span>
);

const CompanyDashboard = () => {
  const { entityId } = useParams();
  const { hasPermission } = useAuth();
  const [summary, setSummary] = useState(null);
  const [brand, setBrand] = useState({});
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const canManage = hasPermission("enterprise.manage");

  const load = useCallback(async () => {
    try {
      const [s, b, bs] = await Promise.all([
        api.get(`/enterprise/entities/${entityId}/summary`),
        api.get(`/brand/${entityId}`).catch(() => ({ data: {} })),
        api.get(`/business-settings/${entityId}`).catch(() => ({ data: {} })),
      ]);
      setSummary(s.data);
      setBrand(b.data || {});
      setSettings(bs.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load company");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveBrand = async (patch) => {
    try {
      const { data } = await api.post("/brand", { entity_id: entityId, ...brand, ...patch });
      setBrand(data);
      toast.success("Brand saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.post("/business-settings", { entity_id: entityId, ...settings, ...patch });
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  if (loading || !summary) {
    return (
      <Layout>
        <div className="flex justify-center py-32">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      </Layout>
    );
  }

  const e = summary.entity;
  const c = summary.counts;

  return (
    <Layout>
      <Link to="/companies" className="inline-flex items-center gap-2 text-sm text-textmuted hover:text-champagne mb-5">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        Companies
      </Link>

      <section className="regal-card p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-champagne" strokeWidth={1.4} />
              <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-textmain">
                {e.name}
              </h1>
            </div>
            <div className="flex items-center gap-3 mt-3 flex-wrap text-xs text-textmuted">
              <span>{e.entity_type}</span>
              <span>·</span>
              <span>{e.status}</span>
              <span>·</span>
              <HealthBadge health={summary.health} />
            </div>
            {e.description && (
              <p className="text-textmuted mt-4 max-w-3xl leading-relaxed">{e.description}</p>
            )}
          </div>
          <Link
            to={`/editor?entity=${entityId}`}
            className="inline-flex items-center gap-2 border border-champagne/40 text-champagne px-4 py-2 rounded-md text-sm hover:bg-champagne/10 transition-colors"
            data-testid="open-editor-link"
          >
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
            Open Site Editor
          </Link>
        </div>
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        {[
          ["Tasks open", c.tasks_open, ListChecks],
          ["Overdue", c.tasks_overdue, Activity],
          ["Approvals", c.approvals_pending, Gavel],
          ["Issues", c.issues_open, Wrench],
          ["Risks open", c.risks_open, ShieldAlert],
          ["Projects", c.projects_active, ListChecks],
          ["KPIs", c.kpis, Activity],
        ].map(([label, value, Icon]) => (
          <div key={label} className="regal-card p-4">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow text-[10px]">{label}</span>
              <Icon className="w-3.5 h-3.5 text-champagne" strokeWidth={1.5} />
            </div>
            <p className="font-sans text-2xl font-light mt-1.5">{value}</p>
          </div>
        ))}
      </section>

      {summary.children?.length > 0 && (
        <section className="mb-8">
          <h2 className="font-heading text-2xl mb-3">Divisions & Programs</h2>
          <div className="regal-card overflow-hidden">
            <div className="divide-y divide-seam">
              {summary.children.map((child) => (
                <Link
                  key={child.id}
                  to={`/companies/${child.id}`}
                  className="block px-5 py-3 hover:bg-elevated/40"
                >
                  <p className="text-textmain">{child.name}</p>
                  <p className="text-[11px] text-textmuted mt-0.5">{child.entity_type} · {child.status}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {canManage && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="regal-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-champagne" strokeWidth={1.5} />
              <h3 className="font-heading text-xl">Business settings</h3>
            </div>
            {[
              ["dba", "DBA"],
              ["industry", "Industry"],
              ["phone", "Phone"],
              ["email", "Email"],
              ["address", "Address"],
              ["hours", "Hours"],
            ].map(([k, label]) => (
              <div key={k} className="mb-3">
                <label className="block label-eyebrow mb-1">{label}</label>
                <input
                  value={settings[k] || ""}
                  onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
                  onBlur={() => saveSettings({ [k]: settings[k] || "" })}
                  data-testid={`settings-${k}`}
                  className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm focus:outline-none focus:border-champagne"
                />
              </div>
            ))}
          </div>

          <div className="regal-card p-6">
            <h3 className="font-heading text-xl mb-4">Brand profile</h3>
            {[
              ["logo_url", "Logo URL"],
              ["slogan", "Slogan"],
              ["primary_color", "Primary color (hex)"],
              ["secondary_color", "Secondary color (hex)"],
              ["footer_text", "Footer text"],
              ["contact_email", "Contact email"],
              ["contact_phone", "Contact phone"],
            ].map(([k, label]) => (
              <div key={k} className="mb-3">
                <label className="block label-eyebrow mb-1">{label}</label>
                <input
                  value={brand[k] || ""}
                  onChange={(e) => setBrand({ ...brand, [k]: e.target.value })}
                  onBlur={() => saveBrand({ [k]: brand[k] || "" })}
                  data-testid={`brand-${k}`}
                  className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm focus:outline-none focus:border-champagne"
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
};

export default CompanyDashboard;
